import {
    Component,
    ElementRef,
    ChangeDetectorRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { FormulaInputPicker } from './formula-input-picker';

/**
 * show a selected date in monthly calendar
 * Each filteredList item has the following property in addition to data itself
 *   1. displayValue as string e.g. Allen Kim
 *   2. dataValue as any e.g.
 */
@Component({
    selector: 'app-formula-input-picker',
    template: `
        <div #autoCompleteContainer class="formula-input-picker">
          <!-- keyword input -->
          @if (showInputTag) {
            <input
              #autoCompleteInput class="keyword"
              [attr.autocomplete]="autocomplete ? 'null' : 'off'"
              placeholder="{{placeholder}}"
              (focus)="showDropdownList($event)"
              (blur)="blurHandler($event)"
              (keydown)="inputElKeyHandler($event)"
              (input)="reloadListInDelay($event)"
              [(ngModel)]="keyword"/>
          }
          <i class="fas fa-search form-control-feedback"></i>
        
        
          <!-- dropdown that user can select -->
          @if (dropdownVisible) {
            <ul [class.empty]="emptyList">
              @if (isLoading && loadingTemplate) {
                <li class="loading"
                [innerHTML]="loadingTemplate"></li>
              }
              @if (isLoading && !loadingTemplate) {
                <li class="loading">{{loadingText}}</li>
              }
              @if (minCharsEntered && !isLoading && !filteredList.length) {
                <li
                  (mousedown)="selectOne('')"
                  class="no-match-found">{{noMatchFoundText || 'No Result Found'}}
                </li>
              }
              @if (headerItemTemplate && filteredList.length) {
                <li class="header-item"
                [innerHTML]="headerItemTemplate"></li>
              }
              @if (blankOptionText && filteredList.length) {
                <li
                  (mousedown)="selectOne('')"
                  class="blank-item">{{blankOptionText}}
                </li>
              }
              @for (item of filteredList; track trackByIndex(i, item); let i = $index) {
                <li
                  (mousedown)="selectOne(item)"
                  [isSelected]="(i === itemIndex)"
                  [option]="item" app-formula-input-option>
                </li>
              }
            </ul>
          }
        
        </div>`,
    providers: [FormulaInputPicker],
    styles: [`
        @keyframes slideDown {
            0% {
                transform: translateY(-10px);
            }
            100% {
                transform: translateY(0px);
            }
        }

        .ngui-auto-complete {
            background-color: transparent;
        }

        .formula-input-picker > input {
            outline: 0;
            padding: 4px 42.5px 4px 10px;
            line-height: 1.42857143;
            box-sizing: border-box;
            background-clip: content-box;
            border-radius: 2px 2px 0 0;
            border: 1px solid #dedede;
            height: 30px;
            display: block;
            width: 100%;
            font-size: 12px;
            box-shadow: inset 0 1px 1px rgba(0,0,0,.075);
            transition: border-color .15s ease-in-out,box-shadow .15s ease-in-out;
        }

        .formula-input-picker > input:focus {
            border-color: #29507c;
            outline: 0;
            box-shadow: inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(41,80,124,.6);
        }

        .formula-input-picker .form-control-feedback {
            width: 30px;
            height: 30px;
            line-height: 30px;
        }

        .formula-input-picker > ul {
            background-color: #fff;
            margin: 0;
            width: 100%;
            overflow-y: auto;
            list-style-type: none;
            padding: 0;
            border: 1px solid #ccc;
            box-sizing: border-box;
            animation: slideDown 0.1s;
            padding: 0;
            max-height: 600px;
            border: 1px solid rgba(0,0,0,.15);
            border-radius: 0 0 4px 4px;
            box-shadow: 0 6px 12px rgba(0,0,0,.175);
        }

        .formula-input-picker > ul.empty {
            display: none;
        }

        .formula-input-picker > ul li {
            padding: 2px 5px;
            border-bottom: 1px solid #eee;
        }

        .formula-input-picker > ul li.selected {
            background-color: #ccc;
        }

        .formula-input-picker > ul li:last-child {
            border-bottom: none;
        }

        .formula-input-picker > ul li:not(.header-item):hover {
            background-color: #ccc;
        }`
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class FormulaInputPickerComponent implements OnInit {

    /**
     * public input properties
     */
    @Input('autocomplete') public autocomplete = false;
    @Input('list-formatter') public listFormatter: (arg: any) => string;
    @Input('source') public source: any;
    @Input('path-to-data') public pathToData: string;
    @Input('min-chars') public minChars = 0;
    @Input('placeholder') public placeholder: string;
    @Input('blank-option-text') public blankOptionText: string;
    @Input('no-match-found-text') public noMatchFoundText: string;
    @Input('accept-user-input') public acceptUserInput = true;
    @Input('loading-text') public loadingText = 'Loading';
    @Input('loading-template') public loadingTemplate = null;
    @Input('max-num-list') public maxNumList: number;
    @Input('show-input-tag') public showInputTag = true;
    @Input('show-dropdown-on-init') public showDropdownOnInit = false;
    @Input('tab-to-select') public tabToSelect = true;
    @Input('match-formatted') public matchFormatted  = false;
    @Input('auto-select-first-item') public autoSelectFirstItem = false;
    @Input('select-on-blur') public selectOnBlur = false;
    @Input('re-focus-after-select') public reFocusAfterSelect = true;
    @Input('header-item-template') public headerItemTemplate = null;
    @Input('ignore-accents') public ignoreAccents = true;

    @Output() public valueSelected = new EventEmitter();
    @Output() public customSelected = new EventEmitter();
    @Output() public textEntered = new EventEmitter();

    @ViewChild('autoCompleteInput') public autoCompleteInput: ElementRef;
    @ViewChild('autoCompleteContainer', { static: true }) public autoCompleteContainer: ElementRef;

    public dropdownVisible = false;
    public isLoading = false;

    public filteredList: any[] = [];
    public minCharsEntered = false;
    public itemIndex: number = null;
    public keyword: string;

    private el: HTMLElement; // this component  element `<app-formula-input-picker>`
    private timer: number;

    private delay = (function() {
        return function(callback: any, ms: number) {
            clearTimeout(this.timer);
            this.timer = setTimeout(callback, ms);
        };
    })();
    private selectOnEnter = false;

    /**
     * constructor
     */
    constructor(elementRef: ElementRef, public autoComplete: FormulaInputPicker, private _changeDetector: ChangeDetectorRef) {
        this.el = elementRef.nativeElement;
    }

    /**
     * user enters into input el, shows list to select, then select one
     */
    ngOnInit(): void {
        this.autoComplete.source = this.source;
        this.autoComplete.pathToData = this.pathToData;
        this.autoComplete.listFormatter = (x) => x.name;
        if (this.autoSelectFirstItem) {
            this.itemIndex = 0;
        }
        setTimeout(() => {
            if (this.autoCompleteInput && this.reFocusAfterSelect) {
                this.autoCompleteInput.nativeElement.focus();
            }
            if (this.showDropdownOnInit) {
                this.showDropdownList({target: {value: ''}});
            }
        });
    }

    public isSrcArr(): boolean {
        return Array.isArray(this.source);
    }

    public reloadListInDelay = (evt: any): void => {
        const delayMs = this.isSrcArr() ? 10 : 500;
        const keyword = evt.target.value;

        // executing after user stopped typing
        this.delay(() => this.reloadList(keyword), delayMs);
    }

    public showDropdownList(event: any): void {
        this.dropdownVisible = true;
        this.reloadList(event.target.value);
    }

    public hideDropdownList(): void {
        this.selectOnEnter = false;
        this.dropdownVisible = false;
    }

    public findItemFromSelectValue(selectText: string): any {
        const matchingItems = this.filteredList.filter((item) => ('' + item) === selectText);
        return matchingItems.length ? matchingItems[0] : null;
    }

    public reloadList(keyword: string): void {

        this.filteredList = [];
        if (keyword.length < (this.minChars || 0)) {
            this.minCharsEntered = false;
            return;
        } else {
            this.minCharsEntered = true;
        }

        if (this.isSrcArr()) {    // local source
            this.isLoading = false;
            this.filteredList = this.autoComplete.filter(this.source, keyword, this.matchFormatted, this.ignoreAccents);
            if (this.maxNumList) {
                this.filteredList = this.filteredList.slice(0, this.maxNumList);
            }
            this._changeDetector.detectChanges();

        } else {                 // remote source
            this.isLoading = true;

            if (typeof this.source === 'function') {
                // custom function that returns observable
                this.source(keyword).subscribe(
                    (resp) => {

                        if (this.pathToData) {
                            const paths = this.pathToData.split('.');
                            paths.forEach((prop) => resp = resp[prop]);
                        }

                        this.filteredList = resp;
                        if (this.maxNumList) {
                            this.filteredList = this.filteredList.slice(0, this.maxNumList);
                        }
                    },
                    (error) => null,
                    () => this.isLoading = false // complete
                );
            } else {
                // remote source

                this.autoComplete.getRemoteData(keyword).subscribe((resp) => {
                        this.filteredList = resp ? resp : [];
                        if (this.maxNumList) {
                            this.filteredList = this.filteredList.slice(0, this.maxNumList);
                        }
                    },
                    (error) => null,
                    () => this.isLoading = false // complete
                );
            }
        }
    }

    public selectOne(data: any) {
        if (!!data || data === '') {
            this.valueSelected.emit(data);
        } else {
            this.customSelected.emit(this.keyword);
        }
    }

    public enterText(data: any) {
        this.textEntered.emit(data);
    }

    public blurHandler(evt: any) {
        if (this.selectOnBlur) {
            this.selectOne(this.filteredList[this.itemIndex]);
        }

        this.hideDropdownList();
    }

    public inputElKeyHandler = (evt: any) => {
        const totalNumItem = this.filteredList.length;

        switch (evt.keyCode) {
            case 27: // ESC, hide auto complete
                this.selectOnEnter = false;
                this.selectOne(undefined);
                break;

            case 38: // UP, select the previous li el
                if (0 === totalNumItem) {
                    return;
                }
                this.selectOnEnter = true;
                this.itemIndex = (totalNumItem + this.itemIndex - 1) % totalNumItem;
                this.scrollToView(this.itemIndex);
                break;

            case 40: // DOWN, select the next li el or the first one
                if (0 === totalNumItem) {
                    return;
                }
                this.selectOnEnter = true;
                this.dropdownVisible = true;
                let sum = this.itemIndex;
                sum = (this.itemIndex === null) ? 0 : sum + 1;
                this.itemIndex = (totalNumItem + sum) % totalNumItem;
                this.scrollToView(this.itemIndex);
                break;

            case 13: // ENTER, choose it!!
                if (this.selectOnEnter) {
                    this.selectOne(this.filteredList[this.itemIndex]);
                }
                evt.preventDefault();
                break;

            case 9: // TAB, choose if tab-to-select is enabled
                if (this.tabToSelect) {
                    this.selectOne(this.filteredList[this.itemIndex]);
                }
                break;
        }
    }

    public scrollToView(index) {
        const container = this.autoCompleteContainer.nativeElement;
        const ul = container.querySelector('ul');
        const li = ul.querySelector('li');  // just sample the first li to get height
        const liHeight = li.offsetHeight;
        const scrollTop = ul.scrollTop;
        const viewport = scrollTop + ul.offsetHeight;
        const scrollOffset = liHeight * index;
        if (scrollOffset < scrollTop || (scrollOffset + liHeight) > viewport) {
            ul.scrollTop = scrollOffset;
        }
    }

    public trackByIndex(index, item) {
        return index;
    }

    get emptyList(): boolean {
        return !(
            this.isLoading ||
            (this.minCharsEntered && !this.isLoading && !this.filteredList.length) ||
            (this.filteredList.length)
        );
    }

}
